import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, type TypeChecker } from '@attain-apps/type-guards'

import * as models from '../models'

import { Value } from './value'

/**
 * Concrete data in the structure of a particular {@link models.Entity} or
 * {@link models.Snapshot}
 */
export interface Record {
  // TODO: creationDate, updateDate
  /**
   * Required if this {@link Record} is of an {@link models.Entity} but not a
   * root {@link models.Entity}
   */
  parentId?: Record.Id
  purposes: readonly PartialId[]
  /** @see {@link Record.Values} */
  values: Record.Values
}
export namespace Record {
  /**
   * Identifies a {@link Record}
   */
  export type Id = PartialId
  export namespace Id {
    export const isId: TypeChecker<Id> =
      PartialId.isPartialId.usingAlias('dms.Record.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)

    /**
     * Key on {@link Record.Values}
     */
    export const KEY =
      // NOTE: using directly causes issues due to circular dependency
      '!id' satisfies typeof models.Attribute.Input.FOR_ID.partialId
  }

  /**
   * Information needed to create a {@link Record}
   */
  export type Input = Partial<
    Replace.Properties<
      Replace.Properties<Record, 'values', Values.Input>,
      'parentId',
      string
    >
  >
  export namespace Input {
    /**
     * Produces a {@link Record.Input.WithValidValues} from a given
     * {@link Record.Input}
     */
    export type WithValidValues = Required.Semi<
      Overwrite<
        Replace.Properties<Input, 'values', Values.Input.WithValidIds>,
        Pick<Record, 'parentId'>
      >,
      'values'
    >
    export namespace WithValidValues {
      /**
       * Produces a {@link Record.Input.WithValidValues} from a given
       * {@link Record.Input}
       */
      export function from(input: Readonly<Input>): WithValidValues {
        return {
          ...input,
          // NOTE: any falsish (including empty string) should be treated as
          // undefined since that is usually what is intended when that occurs
          parentId: input.parentId ? PartialId.from(input.parentId) : undefined,
          values: Values.Input.WithValidIds.from(input.values),
        }
      }
    }
  }

  /**
   * Contains a value for each {@link models.Attribute} for this {@link Record}
   */
  export type Values = Partial<globalThis.Record<Values.Key, Value>> & {
    /**
     * IDs are always {@link PartialId}
     */
    [Record.Id.KEY]: PartialId
  }
  export namespace Values {
    /**
     * Represents the {@link PartialId} of an attribute
     */
    export type Key = PartialId

    // TODO: currently this is just the raw row from Postgres. However, for
    // references/ancestors, we shouldn't include all of the columns. Instead, we
    // should specify `{ "entityId", "recordId" }` and then on write, we should
    // get the ancestors
    /**
     * Input for a {@link Record}
     *
     * __NOTE:__ the ID can be `string | undefined`. If `undefined` a
     * {@link PartialId.Custom} will be generated. If `string` but not
     * {@link PartialId} the `string` will be used as suffix for {@link PartialId.Custom}.
     * Also all foreign keys needs to be updated accordingly
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Input = Partial<
      Replace.Properties<Values, typeof Id.KEY, string>
    >
    export namespace Input {
      /**
       * Same as {@link Input}, but with the ID and any references to other IDs
       * all converted to valid IDs
       *
       * @example
       * // TODO:
       */
      export type WithValidIds = Required.Semi<
        Partial<Values>,
        typeof Record.Id.KEY
      >
      export namespace WithValidIds {
        /**
         * Creates an {@link Input.WithValidIds} from an {@link Input}
         */
        export function from(input: Readonly<Input> | undefined): WithValidIds {
          input ??= {}
          const inputId = input[Record.Id.KEY]
          const inputWithValidIds: WithValidIds = {
            ...input,
            [Record.Id.KEY]: PartialId.from(inputId),
          }
          for (const key in inputWithValidIds) {
            // Converts references to proper IDs
            const recordKey = key as keyof typeof inputWithValidIds
            const value = inputWithValidIds[recordKey]
            if (Value.Reference.Default.TYPE.accepts(value)) {
              // Ensures the entity ID is a proper ID or partial ID
              value.entityId =
                value.entityId &&
                (PartialId.isPartialId(value.entityId) ||
                models.Entity.Id.isId(value.entityId)
                  ? value.entityId
                  : PartialId.Custom.create(value.entityId))
              // Ensures the record ID is a partial ID
              value.recordId =
                value.recordId &&
                (PartialId.isPartialId(value.recordId)
                  ? value.recordId
                  : PartialId.Custom.create(value.recordId))
            }
          }

          return inputWithValidIds
        }
      }
    }
  }
}
